import * as PR from "../../../prime-react/index";
import { useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import ImageDisplay from "../../ImageDisplay";
import { getCategoryByIdAPI, saveExternalLinksAPI } from "../../../services/api";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../../../context/ToastContext";
import { allFileSupportedFormats, fileUploadLimit, trimFileName, validationMessages } from "../../../utils/reuse";

const EditCategory = ({ editCategoryDisplay, setEditCategoryDisplay, getCategories, selectedCategory }) => {
    const dispatch = useDispatch();
    const { showToast } = useToast();
    const sessionData = useSelector(state => state.auth.sessionData);
    const headers = useMemo(() => ({ sessionid: sessionData.sessionId }), [sessionData.sessionId]);
    const bottomRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [showError, setshowError] = useState('');

    const [links, setLinks] = useState([]);
    const [files, setFiles] = useState([]);

    const [initialValues, setInitialValues] = useState({
        categoryName: '',
        links: [],
        files: []
      });

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            links: Yup.array().of(
                Yup.object().shape({
                    description: Yup.string()
                        .required('Description is required')
                        .min(3, 'Description must be at least 3 characters')
                        .max(100, 'Description cannot exceed 100 characters'),
                    url: Yup.string()
                        .required("URL is required")
                        .matches(
                            /^(https?:\/\/)/i,
                            "Only http or https URLs are allowed"
                        )
                        .url("Invalid URL format"),
                    fileFieldKey: Yup.string().nullable()
                  }).test(
                    "url-or-file",
                    "Either URL or file is required",
                    (val) => !!val.url || !!val.fileFieldKey
                )
            ),
            files: Yup.array().of(
                Yup.object().shape({
                    id: Yup.number().optional(),
                    description: Yup.string()
                        .required('Description is required')
                        .min(3, 'Description must be at least 3 characters')
                        .max(100, 'Description cannot exceed 100 characters'),
                    file: Yup.mixed().when("id", {
                        is: (id) => !id, // New rows only
                        then: (schema) =>
                            schema
                                .required("File is required")
                                .test(
                                    "fileType",
                                    "Unsupported format",
                                    (value) => !value || allFileSupportedFormats.includes(value.type)
                                )
                                .test(
                                    "fileSize",
                                    validationMessages.sizeExceed,
                                    (value) => !value || value.size <= fileUploadLimit.value
                                ),
                        otherwise: (schema) => schema.notRequired(),
                    }),
                })
            )
        }),
        onSubmit: (values) => {
            const formData = new FormData();
            const payload = {
                links: [],
            };
            let fileCounter = 1;

            // Process links (with url)
            values.links.forEach((link) => {
                payload.links.push({
                    ...(link.id && { id: link.id }),
                    description: link.description,
                    url: link.url || null
                });
            });

            // Process files
            values.files.forEach((fileItem) => {
                const fileKey = `file${fileCounter}`;
                formData.append(fileKey, fileItem.file);
                payload.links.push({
                    ...(fileItem.id && { id: fileItem.id }),
                    description: fileItem.description,
                    fileFieldKey: fileKey
                });
                fileCounter++;
            });

            // Add JSON blob to formData
            formData.append(
                "data",
                new Blob([JSON.stringify(payload)], { type: "application/json" })
            );

            const categoryID = selectedCategory?.category?.id
            // Submit API
            setFormLoading(true);
            saveExternalLinksAPI(categoryID, formData, headers, dispatch, (response) => {
                if (response.result === "SUCCESS") {
                    showToast("success", "Category updated successfully");
                    setEditCategoryDisplay(false);
                    getCategories();
                } else {
                    const error = response?.error;
                    setshowError(error?.errorMsg || error?.summary)
                    setTimeout(() => {
                        setshowError('')
                    }, 4000)
                }
                setFormLoading(false);
            });
        }
    });

    // Get links and files and display based on selected category ID
    useEffect(() => {
        if (selectedCategory) {
            const categoryId = selectedCategory.category.id;
            setLoading(true);
            getCategoryByIdAPI(categoryId, headers, dispatch, (response) => {
                if (response.result === "SUCCESS") {
                    const result = response.data || [];
                    const linkItems = result
                    .filter(item => item.url && (!item.file || Object.keys(item.file).length === 0))
                    .sort((a, b) => (a.id || 0) - (b.id || 0)); // Sort by ID ascending
          
                  const fileItems = result
                    .filter(item => item.file && Object.keys(item.file).length > 0)
                    .sort((a, b) => (a.id || 0) - (b.id || 0)); // Sort by ID ascending
                    
                    setLinks(linkItems); // for reuse
                    setFiles(fileItems); // for reuse
            
                    setInitialValues({
                        categoryName: selectedCategory?.category?.name || '',
                        links: linkItems,
                        files: fileItems
                      });                      
                } else {
                    setLinks([]);
                    setFiles([]);
                    const error = response.error;
                    setshowError(error?.errorMsg || error?.summary)
                    setTimeout(() => {
                        setshowError('')
                    }, 4000)
                }
                setLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, headers, selectedCategory]);

    const handleDeleteLink = (index) => {
        const updatedLinks = [...formik.values.links];
        updatedLinks.splice(index, 1);
        formik.setFieldValue("links", updatedLinks);
    };

    const handleDeleteFile = (index) => {
        const updatedFiles = [...formik.values.files];
        updatedFiles.splice(index, 1);
        formik.setFieldValue("files", updatedFiles);
    };

    const handleAddNewLink = () => {
        const updatedLinks = [...formik.values.links, { description: "", url: "" }];
        formik.setFieldValue("links", updatedLinks);
    };

    const handleAddNewFile = () => {
        const updatedFiles = [...formik.values.files, { description: "", file: null }];
        formik.setFieldValue("files", updatedFiles);
        scrollToBottom();
    };

    // Scroll to bottom after DOM update
    const scrollToBottom = () => {
        setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    };

    const handleDialogClose = () => {
        setEditCategoryDisplay(false);
      
        // Reset form values to original links and files
        formik.setValues({
          categoryName: selectedCategory?.category?.name || '',
          links: links,
          files: files
        });
      
        formik.setTouched({}); // Clear validation state
        formik.setErrors({});  // Clear error messages
      };
      
    return (
        <>
            <PR.Dialog header="Edit Category" visible={editCategoryDisplay} modal={true}
                blockScroll={true} draggable={false}
                className="external-links-dialog edit"
                onHide={handleDialogClose}>
                {/* Wrapper content */}
                <div className="content-wrapper">
                    {loading ? (
                        <div className="col-12 flex flex-column">
                            <div className="flex align-items-center justify-content-center pb-4">
                                <i className="pi pi-spin pi-spinner text-xl"></i>
                            </div>
                        </div>
                    ) :
                        <form autoComplete="off" onSubmit={formik.handleSubmit}>
                            <div className="flex flex-column gap-2">
                                <div className="flex align-items-center gap-2">
                                    <div className="flex flex-column gap-2">
                                        <label htmlFor="name">Icon</label>
                                        <div className="icon-preview">
                                            <ImageDisplay profileImage={selectedCategory?.category?.icon} type="category" />
                                        </div>
                                    </div>
                                    <div className="flex flex-column gap-2 w-full">
                                        <label htmlFor="name">Category Name</label>
                                        <PR.InputText id="name" name="categoryName" aria-describedby="name" className="edit-input" value={formik.values.categoryName} readOnly />
                                    </div>
                                </div>
                            </div>

                            {/* Links Block */}
                            <h3>Links</h3>
                            {formik.values.links.map((link, index) => (
                                <div className="file-flex mt-3" key={index}>
                                    <div className="link-column-1">
                                        <div className="flex flex-column gap-2 w-full">
                                            <label htmlFor={`links[${index}].description`}>Description<span>*</span></label>
                                            <PR.InputText
                                                name={`links[${index}].description`}
                                                value={link.description}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                className="edit-input"
                                                placeholder="Add link description"
                                            />
                                            {formik.touched.links?.[index]?.description && formik.errors.links?.[index]?.description && (
                                                <small className="p-error">{formik.errors.links[index].description}</small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="link-column-2">
                                        <div className="flex flex-column gap-2 w-full">
                                            <label htmlFor={`links[${index}].url`}>Link<span>*</span></label>
                                            <PR.InputText
                                                name={`links[${index}].url`}
                                                value={link.url}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                className="edit-input"
                                                placeholder="Add link here"
                                            />
                                            {formik.touched.links?.[index]?.url && formik.errors.links?.[index]?.url && (
                                                <small className="p-error">{formik.errors.links[index].url}</small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="link-column-3">
                                        <PR.Button
                                            label="Delete"
                                            className="delete-btn"
                                            type="button"
                                            onClick={() => handleDeleteLink(index)}
                                        />
                                    </div>
                                </div>
                            ))}

                            <PR.Button onClick={handleAddNewLink} type="button" className="add-link">+ Add New Link</PR.Button>

                            {/* Files Block */}
                            <h3>Files</h3>
                            {formik.values.files.map((fileItem, index) => {
                                const isNewRow = !fileItem.id;
                                return (
                                    <div className="file-flex mt-3" key={index}>
                                        <div className="file-column-1">
                                            <div className="flex flex-column gap-2 w-full">
                                                <label htmlFor={`files[${index}].description`}>Description<span>*</span></label>
                                                <PR.InputText
                                                    name={`files[${index}].description`}
                                                    value={fileItem.description}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    className="edit-input file-input"
                                                    placeholder="Add file description"
                                                />
                                                {formik.touched.files?.[index]?.description && formik.errors.files?.[index]?.description && (
                                                    <small className="p-error">{formik.errors.files[index].description}</small>
                                                )}
                                            </div>
                                        </div>

                                        <div className="file-column-2">
                                            <div className="flex flex-column gap-2 w-full">
                                                <label>File<span>*</span></label>
                                                {isNewRow ? (
                                                    <>
                                                        <div className="choose-file-upload flex align-items-center">
                                                            <PR.FileUpload
                                                                mode="advanced"
                                                                name={`files[${index}].file`}
                                                                auto
                                                                customUpload
                                                                chooseLabel="Choose File"
                                                                onSelect={(e) => {
                                                                    const file = e.files?.[0];
                                                                    if (file) {
                                                                        const updatedFiles = [...formik.values.files];
                                                                        updatedFiles[index].file = file;
                                                                        formik.setFieldValue("files", updatedFiles);
                                                                    }
                                                                }}
                                                                onBlur={() => {formik.setFieldTouched(`files[${index}].file`, true); scrollToBottom();}}
                                                            />
                                                            <span className="ml-2">
                                                                <span className="block">(PDF, DOC, DOCX, XLS, XLSX, CSV, PNG, JPG or JPEG)</span>
                                                                {validationMessages.sizeExceed}
                                                            </span>
                                                        </div>
                                                        {fileItem.file && (
                                                            <div className="file-name mt-2">
                                                                <i className="pi pi-file"></i>
                                                                <span>{trimFileName(fileItem.file.name)}</span>
                                                            </div>
                                                        )}
                                                        {isNewRow && formik.touched.files?.[index]?.file && formik.errors.files?.[index]?.file && (
                                                            <small className="p-error">{formik.errors.files[index].file}</small>
                                                        )}
                                                    </>
                                                ) : (
                                                    <div className="file-name">
                                                        <i className="pi pi-file"></i>
                                                        <span>{trimFileName(fileItem.file.name)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="file-column-3">
                                            <PR.Button
                                                label="Delete"
                                                className="delete-btn"
                                                type="button"
                                                onClick={() => handleDeleteFile(index)}
                                            />
                                        </div>
                                    </div>
                                );
                            })}

                            <PR.Button onClick={handleAddNewFile} type="button" className="add-link">+ Upload New File</PR.Button>
                            <div ref={bottomRef} />
                            <div className="dialog-footer">
                                <div className="error-message">{showError}</div>
                                <div className="flex align-items-center">
                                    <PR.Button label="Cancel" outlined className="logout-btns cancel" onClick={handleDialogClose} type="button" />
                                    <PR.Button loading={formLoading} label="Save" className="logout-btns click-btn" type="submit"
                                        disabled={!(formik.dirty && formik.isValid) || formLoading || showError }
                                        tooltip={!formik.dirty ? "No changes made" : !formik.isValid ? "Please fix errors" : ""}
                                        tooltipOptions={{ position: "top" }}
                                    />
                                </div>
                            </div>
                        </form>
                    }
                </div>
            </PR.Dialog>
        </>
    )
}

export default EditCategory;